/**
 * Display options of a widget (AD_Widget.DisplayOptions, JSON) and number formatting shared by the widgets and the
 * editor. Pure functions, covered by widget-editor.model.spec.ts.
 */

export type WidgetNumberFormat = 'number' | 'currency' | 'compact' | 'percent';
export type WidgetLegend = 'auto' | 'show' | 'hide';
/** count: whether a higher value is worse (stock of late orders) or better (sales) */
export type WidgetAlertDirection = 'higherWorse' | 'higherBetter';

export interface WidgetDisplayOptions {
  /** bars, columns, lines: series piled up */
  stacked?: boolean;
  /** value written on each bar, point or slice */
  showValues?: boolean;
  /** bars, columns, lines: names under the categories (hidden by default: the tooltip gives them) */
  axisLabels?: boolean;
  legend?: WidgetLegend;
  /** axis titles: categories (x) and values (y) */
  xTitle?: string;
  yTitle?: string;
  /** line: running total */
  cumulative?: boolean;
  format?: WidgetNumberFormat;
  /** ISO 4217 code for the currency format */
  currency?: string;
  alertDirection?: WidgetAlertDirection;
}

const FORMATS: WidgetNumberFormat[] = ['number', 'currency', 'compact', 'percent'];

/** options of a widget; unknown or broken values are dropped (defaults apply) */
export function parseDisplayOptions(json: string | null | undefined): WidgetDisplayOptions {
  if (!json) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return {};
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const o = raw as Record<string, unknown>;
  const out: WidgetDisplayOptions = {};
  if (o['stacked'] === true) out.stacked = true;
  if (o['showValues'] === true) out.showValues = true;
  if (o['axisLabels'] === true) out.axisLabels = true;
  if (o['legend'] === 'show' || o['legend'] === 'hide' || o['legend'] === 'auto') out.legend = o['legend'];
  if (typeof o['xTitle'] === 'string' && o['xTitle'].trim()) out.xTitle = o['xTitle'].trim().slice(0, 60);
  if (typeof o['yTitle'] === 'string' && o['yTitle'].trim()) out.yTitle = o['yTitle'].trim().slice(0, 60);
  if (o['cumulative'] === true) out.cumulative = true;
  if (FORMATS.includes(o['format'] as WidgetNumberFormat)) out.format = o['format'] as WidgetNumberFormat;
  if (typeof o['currency'] === 'string' && /^[A-Z]{3}$/.test(o['currency'])) out.currency = o['currency'];
  if (o['alertDirection'] === 'higherWorse' || o['alertDirection'] === 'higherBetter') out.alertDirection = o['alertDirection'];
  return out;
}

/** JSON to store: only what differs from the defaults, null when nothing does */
export function serializeDisplayOptions(options: WidgetDisplayOptions): string | null {
  const clean = parseDisplayOptions(JSON.stringify(options ?? {}));
  if (clean.legend === 'auto') delete clean.legend;
  if (clean.format === 'number') delete clean.format;
  if (clean.format !== 'currency') delete clean.currency;
  return Object.keys(clean).length ? JSON.stringify(clean) : null;
}

/**
 * Value as text: number (with the unit), currency, compact (1.2k, 3.4M) or percent (the value is already a
 * percentage: 12.5 gives 12.5 %). precision: decimals (null: up to 2).
 */
export function formatNumber(
  value: number,
  options: { format?: WidgetNumberFormat; currency?: string; precision?: number | null; unit?: string | null },
  locale?: string
): string {
  if (value === null || value === undefined || !isFinite(value)) return '';
  const digits = options.precision != null && options.precision >= 0 ? Math.min(options.precision, 10) : null;
  const decimals = digits === null ? { maximumFractionDigits: 2 } : { minimumFractionDigits: 0, maximumFractionDigits: digits };
  const unit = options.unit?.trim() ? ` ${options.unit.trim()}` : '';
  try {
    switch (options.format) {
      case 'currency':
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: options.currency || 'EUR',
          ...(digits === null ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
        }).format(value);
      case 'compact':
        return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value) + unit;
      case 'percent':
        return new Intl.NumberFormat(locale, { style: 'unit', unit: 'percent', ...decimals }).format(value);
      default:
        return new Intl.NumberFormat(locale, decimals).format(value) + unit;
    }
  } catch {
    // unknown currency or locale: plain number
    return String(value) + unit;
  }
}

/** colour state of a count against its levels (no levels: neutral) */
export function levelState(
  value: number,
  normalLevel: number | null | undefined,
  alertLevel: number | null | undefined,
  direction?: WidgetAlertDirection
): 'neutral' | 'normal' | 'warning' | 'alert' {
  const normal = normalLevel ?? 0;
  const alert = alertLevel ?? 0;
  if (!normal && !alert) return 'neutral';
  if (normal === alert) return 'warning';
  // without a choice, the order of the levels says it (alert above normal: higher is worse)
  const higherWorse = direction ? direction === 'higherWorse' : alert > normal;
  if (higherWorse) {
    if (value >= alert) return 'alert';
    if (value <= normal) return 'normal';
    return 'warning';
  }
  if (value <= alert) return 'alert';
  if (value >= normal) return 'normal';
  return 'warning';
}
